// ─────────────────────────────────────────────────────────────────
// STATE GROUPS — rebuilt from verified sources after the earlier
// framework in this file was found to be unsourced/incorrect.
//
// SOURCES (checked against each other, both current as of this
// rebuild):
//   1. Evergreen Safety Council — the official Washington State PEVO
//      training/certification provider. Their reciprocity table is
//      the most detailed, human-curated source for the WA network.
//      https://www.esc.org/pilot-car/pilot-car-reciprocity/
//   2. National Pilot Car Association (NPCA) — a national trade
//      association with a state-by-state regulation database.
//      https://www.pilotcarassociation.org/regulations
//      NOTE: a handful of NPCA's exact numeric thresholds show signs
//      of data-entry/scraping errors (a few implausible outlier
//      figures). Where a number looked wrong, it's omitted here in
//      favor of "verify with state DOT" rather than republished as
//      fact. The certification-required flag itself is corroborated
//      across both sources and is treated as reliable.
//
// REAL STRUCTURE (confirmed by both sources):
//   - 13 states share a genuine reciprocity network: one certificate
//     (Washington's, or certain member states' own) is valid across
//     all 13.
//   - 3 states each require their OWN separate certification with no
//     reciprocity into that network: New York (confirmed zero
//     reciprocity by both sources), Oregon, South Dakota.
//   - The remaining 35 states + DC have no operator certification
//     requirement in law, though most still have their own escort
//     trigger dimensions, insurance minimums, and equipment rules.
//
// Regulations change. This is a snapshot verified against the two
// sources above — re-verify before treating any single figure as
// current, and always confirm with the relevant state DOT before a
// real move.
// ─────────────────────────────────────────────────────────────────

export const GROUPS = {
  reciprocity: {
    id: "reciprocity",
    label: "13-State Reciprocity Network",
    sublabel: "One Certificate, 13 States",
    colorKey: "green",
    icon: "🛡️",
    description: "One certificate — typically Washington's PEVO certification — is recognized across all 13 of these states. This is the closest thing to a single national credential in this industry.",
    keyRule: "Certified once, valid in all 13 — but some member states still require an additional local permit or short local course on top (see each state's note).",
    sourceNote: "Source: Evergreen Safety Council (WA's official PEVO training provider) reciprocity table.",
    states: [
      { code:"AZ", name:"Arizona",        ins:"$1,000,000 CSL", certYears:4, note:"Full reciprocity both directions." },
      { code:"CO", name:"Colorado",       ins:"$1,000,000 CSL", certYears:3, note:"Full reciprocity both directions." },
      { code:"FL", name:"Florida",        ins:"$1,000,000 CSL", certYears:4, note:"Accepts WA cert, but requires an additional defensive-driving course. WA does not accept Florida's cert." },
      { code:"GA", name:"Georgia",        ins:"$500,000 CSL",   certYears:3, note:"Full reciprocity both directions." },
      { code:"KS", name:"Kansas",         ins:"$500,000 CSL",   certYears:4, note:"Accepts WA cert. Kansas has no full certification program of its own — also requires registering for an additional KDOT permit." },
      { code:"MN", name:"Minnesota",      ins:"$1,000,000 CSL", certYears:4, note:"Full reciprocity both directions." },
      { code:"NC", name:"North Carolina", ins:"$1,000,000 CSL", certYears:4, note:"Full reciprocity both directions." },
      { code:"OK", name:"Oklahoma",       ins:"$500,000 CSL",   certYears:3, note:"Full reciprocity both directions." },
      { code:"PA", name:"Pennsylvania",   ins:"$1,000,000 CSL", certYears:4, note:"Accepts WA cert, but only required for Superloads, plus a defensive-driving and flagger course. WA does not accept Pennsylvania's cert." },
      { code:"TX", name:"Texas",          ins:"$500,000 CSL",   certYears:4, note:"Accepts WA cert. Texas only formally requires a flagger certification, but the WA PEVO cert satisfies it." },
      { code:"UT", name:"Utah",           ins:"$1,000,000 CSL", certYears:4, note:"Full reciprocity both directions. Also requires a separate permit for the escort vehicle itself." },
      { code:"VA", name:"Virginia",       ins:"$1,000,000 CSL", certYears:4, note:"Full reciprocity both directions." },
      { code:"WA", name:"Washington",     ins:"$1,000,000 CSL", certYears:4, note:"Origin state of the PEVO certification and reciprocity network." },
    ],
  },
  independent: {
    id: "independent",
    label: "Independent Certification States",
    sublabel: "Own Cert Required — No Reciprocity",
    colorKey: "amber",
    icon: "⭐",
    description: "Each of these states requires its own state-specific certification. A certificate from the 13-state network (or any other state) is not accepted here.",
    keyRule: "Obtain that state's own certification before operating there — no other state's cert substitutes.",
    sourceNote: "New York confirmed zero-reciprocity by both sources. Oregon and South Dakota confirmed cert-required by NPCA; not part of the WA reciprocity table, so treated as independent pending further verification.",
    states: [
      { code:"NY", name:"New York",      ins:"$1,000,000 CSL", certYears:4, note:"Zero reciprocity — confirmed by both sources. Does not accept any other state's certification, and no other state accepts New York's." },
      { code:"OR", name:"Oregon",        ins:"Verify with ODOT", certYears:null, note:"Requires its own state certification. Not listed in the WA reciprocity network — confirm current relationship directly with ODOT." },
      { code:"SD", name:"South Dakota",  ins:"Verify with SDDOT", certYears:null, note:"Requires its own state certification. Not listed in the WA reciprocity network — confirm current relationship directly with SDDOT." },
    ],
  },
  noCertRequired: {
    id: "noCertRequired",
    label: "No Certification Required",
    sublabel: "Escort Rules Still Apply",
    colorKey: "blue",
    icon: "🗺️",
    description: "These states do not require a state-issued operator certification by law — but nearly all still have their own escort-trigger dimensions, insurance minimums, and equipment rules that must be followed. \"No certification\" does not mean \"no rules.\"",
    keyRule: "No exam/cert required, but confirm current escort triggers, insurance minimums, and equipment rules with that state's DOT before every move.",
    sourceNote: "Source: National Pilot Car Association state regulation database. A few exact dimension figures in NPCA's own data show signs of transcription errors — those are marked \"verify with state DOT\" here rather than republished as fact.",
    states: [
      { code:"AL", name:"Alabama",        ins:"$300,000" },
      { code:"AK", name:"Alaska" },
      { code:"AR", name:"Arkansas" },
      { code:"CA", name:"California",     note:"No certification law, but a dense CHP/Caltrans permit-and-escort system (weekend/holiday travel bans, tiered escort triggers) applies instead." },
      { code:"CT", name:"Connecticut" },
      { code:"DE", name:"Delaware" },
      { code:"DC", name:"Washington DC" },
      { code:"HI", name:"Hawaii",         note:"Weekend/holiday moves restricted; permits handled by island-specific DOT offices." },
      { code:"ID", name:"Idaho" },
      { code:"IL", name:"Illinois",       ins:"$500,000" },
      { code:"IN", name:"Indiana" },
      { code:"IA", name:"Iowa" },
      { code:"KY", name:"Kentucky" },
      { code:"LA", name:"Louisiana" },
      { code:"ME", name:"Maine" },
      { code:"MD", name:"Maryland" },
      { code:"MA", name:"Massachusetts" },
      { code:"MI", name:"Michigan" },
      { code:"MS", name:"Mississippi" },
      { code:"MO", name:"Missouri" },
      { code:"MT", name:"Montana" },
      { code:"NE", name:"Nebraska" },
      { code:"NV", name:"Nevada" },
      { code:"NH", name:"New Hampshire" },
      { code:"NJ", name:"New Jersey" },
      { code:"NM", name:"New Mexico" },
      { code:"ND", name:"North Dakota" },
      { code:"OH", name:"Ohio" },
      { code:"RI", name:"Rhode Island" },
      { code:"SC", name:"South Carolina" },
      { code:"TN", name:"Tennessee" },
      { code:"VT", name:"Vermont" },
      { code:"WV", name:"West Virginia" },
      { code:"WI", name:"Wisconsin" },
      { code:"WY", name:"Wyoming" },
    ],
  },
};

// ─── Lookup helpers ─────────────────────────────────────────────────

export function findStateMeta(code) {
  for (const g of Object.values(GROUPS)) {
    const s = g.states.find((x) => x.code === code);
    if (s) return { ...s, group: g.id, groupLabel: g.label, groupColorKey: g.colorKey };
  }
  return null;
}

export function getStateFlags(code) {
  const meta = findStateMeta(code);
  return {
    inReciprocityNetwork: meta?.group === "reciprocity",
    independentCert: meta?.group === "independent",
    noCertRequired: meta?.group === "noCertRequired",
  };
}

export const ALL_STATES = Object.values(GROUPS).flatMap((g) =>
  g.states.map((s) => ({ ...s, group: g.id, groupLabel: g.label, groupColorKey: g.colorKey }))
);
